import React, { useState } from 'react';
import { IconButton, Typography, Box } from '@mui/material';
import { SmallCopyIcon, Tooltip } from 'framework/ui/mui-builder';
import CopyLinkWithInterviewer from './CopyLinkWithInterviewer';
import useInterviewSchedulerLink from './useInterviewSchedulerLink';
import { InputLoader } from 'framework/ui/mui-builder';
import { keyframes } from '@mui/material';
import { useShowContext } from 'framework/show';

const shake = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
`;

const CopyInterviewSchedulerLink = ({
  currentApplicationId,
  personalEmail,
  isAdditional = false,
}: {
  currentApplicationId: number;
  personalEmail?: Nullable<string>;
  isAdditional?: boolean;
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [error, setError] = useState(false);
  const { data } = useShowContext();
  const { copyAction, isCopied, getLink, isLoading } = useInterviewSchedulerLink(currentApplicationId);
  const { isCustomPostion } = data.getData();

  const handleClick = async () => {
    if (isCopied || !personalEmail) return;

    if (isCustomPostion) {
      setIsFormOpen(true);
      return;
    }

    try {
      const hash = await getLink();
      await copyAction(String(hash));
      setError(false);
    } catch {
      setError(true);
      setTimeout(() => setError(false), 600);
    }
  };
  const title = isAdditional ? 'Link to additional interview scheduler' : 'Link to interview scheduler';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: error ? 'error.main' : 'common.inputIcons',
          transition: 'color 0.3s ease-in-out',
          animation: error ? `${shake} 0.3s ease-in-out` : 'none',
        }}
        onClick={handleClick}
      >
        {personalEmail && (
          <IconButton
            sx={{
              p: 0,
              mr: '5px',
              width: '22px',
              backgroundColor: error ? 'background.error' : 'transparent',
              transition: 'background-color 0.3s ease-in-out',
            }}
          >
            {isLoading ? <InputLoader /> : <SmallCopyIcon sx={{ width: '18px' }} />}
          </IconButton>
        )}
        {!personalEmail && !isAdditional && <Typography variant="body2">Candidate doesn`t have an email.</Typography>}

        {personalEmail && (
          <Tooltip
            placement="top"
            title={personalEmail && isCustomPostion && 'Select an interviewer for custom specializations.'}
          >
            <div>
              <Typography variant="body2">{isCopied ? 'Copied!' : title}</Typography>
            </div>
          </Tooltip>
        )}
      </Box>

      {personalEmail && (
        <CopyLinkWithInterviewer
          isFormOpen={isFormOpen}
          setIsFormOpen={setIsFormOpen}
          getLink={getLink}
          copyAction={copyAction}
          isAdditional={isAdditional}
        />
      )}
    </Box>
  );
};

export default CopyInterviewSchedulerLink;
